import React, { useEffect, useMemo, useState, useCallback, useRef, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import {
  BookOpen, ChevronLeft, ChevronRight, X, FileText, AlertCircle, Loader2,
} from 'lucide-react';
import api, { API_BASE } from '@/api/client';
import Seo from '@/seo/Seo';

function pageImageUrl(leafletId, pageNumber, bust) {
  const q = bust ? `?v=${encodeURIComponent(bust)}` : '';
  return `${API_BASE}/public/leaflets/${leafletId}/pages/${pageNumber}${q}`;
}


// ---------- Single flipbook page ----------
// react-pageflip wraps children in its own flip containers, so each child must
// forward its DOM ref back up. We render the A5 image on a plain white sheet
// and let the library handle shadows + 3D perspective.
const FlipPage = forwardRef(function FlipPage({ leafletId, pageNumber, bust, isCover }, ref) {
  return (
    <div
      ref={ref}
      className={`flip-page relative bg-white ${isCover ? 'flip-page-cover' : ''}`}
      data-density={isCover ? 'hard' : 'soft'}
      data-testid={`flip-page-${pageNumber}`}
    >
      <img
        src={pageImageUrl(leafletId, pageNumber, bust)}
        alt={`Page ${pageNumber}`}
        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
        draggable={false}
      />
      <span className="absolute bottom-1.5 right-2 text-[10px] font-semibold text-moj-charcoal/60 bg-white/80 rounded px-1.5 py-0.5 pointer-events-none">
        {pageNumber}
      </span>
    </div>
  );
});


// A5 aspect ratio — 148 × 210 mm → height = width × √2 (≈ 1.4142).
const A5_RATIO = 1.4142;

// Compute the largest single-page width/height that fits inside the given
// container while preserving A5 proportions. In landscape mode the book shows
// TWO pages side-by-side, so each page can only use half the width.
function fitA5Page(containerW, containerH, isPortrait) {
  const availableW = isPortrait ? containerW : containerW / 2;
  // If we size to the full available width, does the resulting height fit?
  const byWidthH = availableW * A5_RATIO;
  let pageW;
  let pageH;
  if (byWidthH <= containerH) {
    pageW = availableW;
    pageH = byWidthH;
  } else {
    pageH = containerH;
    pageW = containerH / A5_RATIO;
  }
  return {
    width: Math.max(220, Math.floor(pageW)),
    height: Math.max(310, Math.floor(pageH)),
  };
}


// ---------- Reader modal ----------
// Uses react-pageflip to render a realistic 3D booklet. The library handles:
//   • Click-and-drag page corners to flip
//   • Swipe gestures on touch devices
//   • A standalone front cover AND a standalone back cover when the page count is even (showCover)
// We keep the Prev/Next buttons as accessible fallbacks and drive them programmatically.
//
// Sizing strategy: react-pageflip's `size="stretch"` is unreliable inside
// flexbox parents, often clamping to its internal fallback (~212×300). We
// instead MEASURE the available viewport ourselves and pass explicit `width`
// and `height` props based on A5 aspect ratio. The flipbook is remounted
// (via `key`) whenever the computed size changes meaningfully, so page turns
// never jitter mid-flip from a resize.
function LeafletReader({ leaflet, onClose }) {
  const flipRef = useRef(null);
  const containerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed library page
  const [isPortrait, setIsPortrait] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  // Explicit page dimensions passed to HTMLFlipBook. Null until first measure.
  const [pageSize, setPageSize] = useState(null);

  const totalPages = leaflet.page_count;
  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages],
  );

  // Measure the container, decide portrait/landscape, and compute explicit
  // A5 page dimensions. We debounce updates by only committing new sizes
  // that differ by at least 2 px to avoid a resize storm (which would
  // otherwise remount the flipbook repeatedly).
  const recalc = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return; // not laid out yet
    const portrait = window.innerWidth < 768;
    const next = fitA5Page(rect.width, rect.height, portrait);
    setIsPortrait((prev) => (prev === portrait ? prev : portrait));
    setPageSize((prev) => {
      if (prev && Math.abs(prev.width - next.width) < 2 && Math.abs(prev.height - next.height) < 2) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(recalc);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(recalc));
    window.addEventListener('resize', recalc);
    window.addEventListener('orientationchange', recalc);
    let ro;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(recalc);
      ro.observe(containerRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', recalc);
      window.removeEventListener('orientationchange', recalc);
      if (ro) ro.disconnect();
    };
  }, [recalc]);

  // Keyboard navigation + body scroll lock.
  useEffect(() => {
    const onKey = (e) => {
      const api = flipRef.current?.pageFlip?.();
      if (!api) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault(); api.flipNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault(); api.flipPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault(); onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Preload neighbouring pages for smooth flips.
  useEffect(() => {
    const toPreload = [currentPage + 1, currentPage + 2, currentPage - 1].filter(
      (i) => i >= 0 && i < totalPages,
    );
    toPreload.forEach((i) => {
      const img = new Image();
      img.src = pageImageUrl(leaflet.id, i + 1, leaflet.updated_at);
    });
  }, [currentPage, totalPages, leaflet.id, leaflet.updated_at]);

  // Build an accurate indicator from the library's state. In portrait mode
  // one page is visible; in landscape mode either one (cover/back) or two.
  const indicator = useMemo(() => {
    const idx = currentPage; // 0-based library page index
    if (totalPages <= 0) return '';
    if (isPortrait) {
      return `Page ${Math.min(idx + 1, totalPages)} of ${totalPages}`;
    }
    // Landscape with showCover=true:
    //   • idx 0 → cover standalone (Page 1)
    //   • idx totalPages-1 → back cover standalone when total pages is even
    //   • Everything else → a spread (idx, idx+1)
    const isCover = idx === 0;
    const isBack = totalPages % 2 === 0 && idx === totalPages - 1;
    if (isCover || isBack) return `Page ${idx + 1} of ${totalPages}`;
    const right = idx + 1 < totalPages ? idx + 2 : idx + 1;
    return `Pages ${idx + 1}–${right} of ${totalPages}`;
  }, [currentPage, totalPages, isPortrait]);

  const atStart = currentPage <= 0;
  const atEnd = currentPage >= totalPages - 1;

  const flipNext = () => flipRef.current?.pageFlip?.()?.flipNext();
  const flipPrev = () => flipRef.current?.pageFlip?.()?.flipPrev();

  return (
    <div
      className="fixed inset-0 z-[100] bg-moj-charcoal flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={`${leaflet.title} reader`}
      data-testid="leaflet-reader"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-white border-b border-white/10 flex-shrink-0">
        <div className="min-w-0 flex items-center gap-3">
          <BookOpen size={20} className="text-moj-gold flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="font-serif text-base sm:text-lg truncate" data-testid="leaflet-reader-title">{leaflet.title}</h2>
            <div className="text-[11px] sm:text-xs text-white/60" data-testid="leaflet-reader-page-indicator">{indicator}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 px-3 min-h-[40px] rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 active:bg-white/30 border border-white/20 touch-manipulation flex-shrink-0"
          data-testid="leaflet-reader-close"
        >
          <X size={16}/> <span className="hidden sm:inline">Close Reader</span><span className="sm:hidden">Close</span>
        </button>
      </div>

      {/* Hint strip */}
      <div className="px-4 sm:px-6 py-1.5 text-center text-[11px] text-white/50 flex-shrink-0 select-none">
        <span className="hidden sm:inline">Drag a page corner, swipe, use the arrow keys or the buttons below to turn pages.</span>
        <span className="sm:hidden">Swipe left/right or tap the arrows to turn pages.</span>
      </div>

      {/* Flipbook area */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden px-2 sm:px-4 py-2 sm:py-4"
        data-testid="leaflet-reader-flipbook-wrap"
      >
        {totalPages > 0 && pageSize && (
          <HTMLFlipBook
            key={`${leaflet.id}-${isPortrait ? 'p' : 'l'}-${pageSize.width}x${pageSize.height}`}
            ref={flipRef}
            // Explicit, measured dimensions — one page is width × height.
            // In landscape mode the book shows two pages side-by-side so the
            // total rendered width is roughly 2 × width.
            width={pageSize.width}
            height={pageSize.height}
            size="fixed"
            minWidth={220}
            maxWidth={1200}
            minHeight={310}
            maxHeight={1700}
            maxShadowOpacity={0.5}
            showCover={true}
            mobileScrollSupport={true}
            usePortrait={isPortrait}
            drawShadow={true}
            flippingTime={700}
            swipeDistance={30}
            useMouseEvents={true}
            onFlip={(e) => setCurrentPage(e.data)}
            onInit={() => setCurrentPage(0)}
            className="leaflet-flipbook"
            style={{ background: 'transparent' }}
            startPage={0}
            autoSize={false}
            clickEventForward={true}
            showPageCorners={true}
            disableFlipByClick={false}
          >
            {pageNumbers.map((p) => (
              <FlipPage
                key={p}
                leafletId={leaflet.id}
                pageNumber={p}
                bust={leaflet.updated_at}
                isCover={p === 1 || (totalPages % 2 === 0 && p === totalPages)}
              />
            ))}
          </HTMLFlipBook>
        )}
      </div>

      {/* Footer nav */}
      <div className="px-4 sm:px-6 py-3 border-t border-white/10 flex items-center justify-between gap-3 text-white flex-shrink-0">
        <button
          type="button"
          onClick={flipPrev}
          disabled={atStart}
          className="inline-flex items-center gap-2 px-4 min-h-[44px] rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/20 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
          data-testid="leaflet-reader-prev"
        >
          <ChevronLeft size={16}/> <span className="hidden sm:inline">Previous Page</span><span className="sm:hidden">Prev</span>
        </button>
        <div className="text-[11px] sm:text-sm text-white/70 font-semibold text-center" data-testid="leaflet-reader-progress">
          {Math.min(currentPage + 1, totalPages)} / {totalPages}
        </div>
        <button
          type="button"
          onClick={flipNext}
          disabled={atEnd}
          className="inline-flex items-center gap-2 px-4 min-h-[44px] rounded-full text-sm font-semibold bg-moj-gold text-moj-purple-deep hover:brightness-110 disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
          data-testid="leaflet-reader-next"
        >
          <span className="hidden sm:inline">Next Page</span><span className="sm:hidden">Next</span> <ChevronRight size={16}/>
        </button>
      </div>
    </div>
  );
}


// ---------- Gallery card ----------
function LeafletCard({ leaflet, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left card-moj overflow-hidden hover:shadow-xl focus-visible:ring-2 focus-visible:ring-moj-gold focus-visible:outline-none transition flex flex-col"
      data-testid={`leaflet-card-${leaflet.id}`}
      aria-label={`Open ${leaflet.title}`}
    >
      {/* Cover thumbnail — strict A5 portrait so the design is never squashed. */}
      <div className="aspect-[1/1.414] bg-moj-cream-soft overflow-hidden relative">
        <img
          src={pageImageUrl(leaflet.id, 1, leaflet.updated_at)}
          alt={`${leaflet.title} — cover`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain bg-white group-hover:scale-[1.02] transition-transform duration-300"
        />
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <h3 className="font-serif text-lg text-moj-purple-deep leading-tight line-clamp-2">{leaflet.title}</h3>
        {leaflet.description && (
          <p className="text-sm text-moj-charcoal/70 line-clamp-3">{leaflet.description}</p>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between text-xs text-moj-charcoal/60">
          <span className="inline-flex items-center gap-1"><FileText size={12}/> {leaflet.page_count} page{leaflet.page_count === 1 ? '' : 's'}</span>
          <span className="inline-flex items-center gap-1 text-moj-purple-deep font-semibold group-hover:text-moj-gold-deep">
            <BookOpen size={14}/> Read
          </span>
        </div>
      </div>
    </button>
  );
}


// ---------- Main page ----------
export default function Leaflets() {
  const [activeId, setActiveId] = useState(null);

  const { data: leaflets = [], isLoading, isError } = useQuery({
    queryKey: ['pub-leaflets'],
    queryFn: async () => (await api.get('/public/leaflets')).data,
  });

  const active = useMemo(
    () => leaflets.find((l) => l.id === activeId) || null,
    [leaflets, activeId],
  );

  // Fetch the full leaflet (page metadata) only once we actually open it.
  const { data: activeFull, isLoading: readerLoading } = useQuery({
    queryKey: ['pub-leaflet', activeId],
    queryFn: async () => (await api.get(`/public/leaflets/${activeId}`)).data,
    enabled: Boolean(activeId),
  });

  return (
    <div>
      <Seo
        title="Leaflets"
        description="Read our free gospel leaflets online. Short, printable tracts introducing Jesus Christ and the Christian faith."
        path="/leaflets"
      />
      <section className="gradient-purple text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-moj-gold/20 flex items-center justify-center">
              <BookOpen size={22} className="text-moj-gold"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-moj-gold font-semibold">Reading Room</div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl">Our Leaflets</h1>
            </div>
          </div>
          <p className="max-w-2xl text-white/80 text-sm sm:text-base">
            Short, printable gospel leaflets in A5 format. Tap any cover to open the reader and turn through pages just like a real booklet.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {isLoading && (
            <div className="flex items-center gap-2 text-moj-charcoal/70 text-sm" data-testid="leaflets-loading">
              <Loader2 size={16} className="animate-spin"/> Loading leaflets…
            </div>
          )}

          {isError && !isLoading && (
            <div className="card-moj p-6 flex items-start gap-3 text-sm text-moj-charcoal" data-testid="leaflets-error">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5"/>
              <span>We couldn't load the leaflets right now. Please try again shortly.</span>
            </div>
          )}

          {!isLoading && !isError && leaflets.length === 0 && (
            <div className="card-moj p-10 text-center text-moj-charcoal/70" data-testid="leaflets-empty">
              <BookOpen size={32} className="mx-auto mb-3 text-moj-purple-deep/40" />
              <p className="font-semibold text-moj-purple-deep">Our leaflet library is on the way.</p>
              <p className="text-sm mt-1">Check back soon — new tracts are added regularly.</p>
              <Link to="/" className="btn-outline mt-4 inline-flex">Back to home</Link>
            </div>
          )}

          {leaflets.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="leaflets-grid">
              {leaflets.map((l) => (
                <LeafletCard key={l.id} leaflet={l} onOpen={() => setActiveId(l.id)} />
              ))}
            </div>
          )}
        </div>
      </section>

      {activeId && (readerLoading || !activeFull) && (
        <div
          className="fixed inset-0 z-[100] bg-moj-charcoal/90 flex items-center justify-center text-white"
          data-testid="leaflet-reader-loading"
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={28} className="animate-spin text-moj-gold"/>
            <div className="text-sm">Opening leaflet…</div>
            <button
              type="button"
              onClick={() => setActiveId(null)}
              className="text-xs text-white/60 hover:text-white underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {activeFull && activeId && (
        <LeafletReader
          leaflet={activeFull}
          onClose={() => setActiveId(null)}
        />
      )}
    </div>
  );
}
